import { Button, Grid2, InputLabel, TextField } from '@mui/material';
import { CommissionType, BookingOrigin } from '@utriper/core';
import { SelectComponent, Title } from '@utriper/core-ui';
import { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

const CommissionForm = ({ commission, onSubmit = () => {}, onClose, contractMembers = [], contractExperiences = [] }) => {
    const { t: translate, i18n } = useTranslation();

    const [isChanged, setIsChanged] = useState(false);

    const feeTypeOptions = useMemo(
        () => [
            ...CommissionType?.getIds().map((id) => {
                const translated = CommissionType.getTranslatedName(CommissionType.types, id, i18n.language);
                return { name: translated?.[0] ?? '', id };
            }),
        ],
        [i18n.language]
    );

    const excludeBookingOriginIds = new Set([BookingOrigin.IDS.undefined, BookingOrigin.IDS.webapp, BookingOrigin.IDS.mobileapp, BookingOrigin.IDS.code]);

    const originOptions = useMemo(() => {
        const ids = BookingOrigin.getIds();

        const filteredIds = ids.filter((id) => !excludeBookingOriginIds.has(id));

        return filteredIds.map((id) => {
            const translated = BookingOrigin.getTranslatedName(BookingOrigin.types, id, i18n.language);
            return { name: translated?.[0] ?? '', id };
        });
    }, [BookingOrigin, i18n.language]);

    const currencyOptions = [
        { name: '', id: '' },
        {
            id: 'COP',
            name: 'COP',
        },
        {
            id: 'USD',
            name: 'USD',
        },
    ];

    const contractMembersOptions = contractMembers?.map((contractMember) => ({
        id: contractMember?.id,
        name: contractMember?.name,
    }));

    const contractExperiencesOptions = [
        { name: '', id: '' },
        ...contractExperiences?.map((contractExperience) => ({
            id: contractExperience?.contractExpId,
            name: contractExperience?.name,
        })),
    ];

    return (
        <Grid2 py={2}>
            <Form
                onSubmit={onSubmit}
                initialValues={commission}
                render={({ handleSubmit, form, values }) => {
                    const { feeType = 0 } = values || {};
                    return (
                        <Grid2 container flexDirection="column" spacing={1}>
                            <Grid2>
                                <Title value={translate('contract.commissions.title')} size="l" />
                            </Grid2>
                            <Grid2 container flexDirection="column" spacing={3}>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('contract.commissions.member')}</InputLabel>
                                    <Field id="contractMemberId" name="contractMemberId">
                                        {({ input }) => (
                                            <SelectComponent
                                                label={translate('contract.commissions.member')}
                                                component={SelectComponent}
                                                values={contractMembersOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    let value = e.target.value;

                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('contract.commissions.experience')}</InputLabel>
                                    <Field id="contractExperienceId" name="contractExperienceId">
                                        {({ input }) => (
                                            <SelectComponent
                                                label={translate('contract.commissions.experience')}
                                                component={SelectComponent}
                                                values={contractExperiencesOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    let value = e.target.value;

                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('contract.commissions.origin')}</InputLabel>
                                    <Field id="originId" name="originId">
                                        {({ input }) => (
                                            <SelectComponent
                                                label={translate('contract.commissions.origin')}
                                                component={SelectComponent}
                                                values={originOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    let value = e.target.value;

                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('contract.commissions.feeType')}</InputLabel>
                                    <Field id="feeType" name="feeType">
                                        {({ input }) => (
                                            <SelectComponent
                                                label={translate('contract.commissions.feeType')}
                                                component={SelectComponent}
                                                values={feeTypeOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    let value = e.target.value;

                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('contract.commissions.feeRate')}</InputLabel>
                                    <Field id="feeRate" name="feeRate">
                                        {({ input }) => (
                                            <TextField
                                                {...input}
                                                type="number"
                                                variant="outlined"
                                                hintText={'feeRate'}
                                                label={translate('contract.commissions.feeRate')}
                                                sx={{ width: '100%' }}
                                                onChange={(e) => {
                                                    let value = parseFloat(e.target.value) || 0;

                                                    if (feeType === 0) {
                                                        if (value > 100) value = 100;
                                                        else if (value < 0) value = 0;
                                                    } else {
                                                        if (value < 0) value = 0;
                                                    }

                                                    value = Math.round(value * 10000) / 10000; // limitar a 4 decimalnes
                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('contract.commissions.currency')}</InputLabel>
                                    <Field id="currency" name="currency">
                                        {({ input }) => (
                                            <SelectComponent
                                                label={translate('contract.commissions.currency')}
                                                component={SelectComponent}
                                                values={currencyOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    let value = e.target.value;

                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 container justifyContent="flex-end">
                                    <Grid2>
                                        <Button
                                            onClick={(e) => {
                                                onClose();
                                                return form.reset(e);
                                            }}
                                            sx={{ color: '#363740 !important', fontWeight: 'bold' }}
                                            variant="outlined">
                                            {translate('btn_cancel')}
                                        </Button>
                                    </Grid2>
                                    <Grid2>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ color: 'white !important', fontWeight: 'bold' }}
                                            disabled={contractMembers?.length === 0 || !isChanged}
                                            onClick={(e) => {
                                                return handleSubmit(e);
                                            }}>
                                            {translate('btn_add')}
                                        </Button>
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        </Grid2>
                    );
                }}
            />
        </Grid2>
    );
};

export default CommissionForm;
